import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectDropdownOptions, selectDropdowns } from '../../../redux/dropdownSlice'
import { setNotification } from '../../../redux/notificationSlice'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { Controller, useForm } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { buildQueryString } from '../../../helpers/formatting'
import { setModal } from "../../../redux/modalSlice";

export default function SensitivePiiViewModal() {
  const [sensitiveData, setSensitiveData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { control, handleSubmit, reset, formState: { errors, isValid } } = useForm()
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const dispatch = useDispatch()
  const requests = useRequest()
  const requestId = useRef(0)

  // Get modal state from Redux (following the pattern from other modals)
  const modal = useSelector((state) => state.modal)
  const isActive = modal.is_active && modal.type === 'sensitive_pii_view_reason_modal'
  // Get props from modal options
  const customerId = modal.options?.customerId
  const type = modal.options?.type
  const onClose = modal.options?.onClose
  const loanId = modal.options?.loanId
  const bankAccountId = modal.options?.bankAccountId
  const documentId = modal.options?.documentId
  const documentName = modal.options?.documentName

  // Get sensitive PII view reason options
  const sensitivePiiViewReason = dropdowns.data?.find((o) => o.code_key === 'sensitive_pii_view_reason')
  const allSensitivePiiViewReasonOptions = sensitivePiiViewReason ?
    dropdownOptions.filter((o) => o.dropdown_id === sensitivePiiViewReason.id).map((o) => ({
      label: o.name,
      value: o.id,
      code_key: o.code_key,
      optional_value_1: o.optional_value_1,
    })) : []

  // Filter options based on type
  const sensitivePiiViewReasonOptions = allSensitivePiiViewReasonOptions.filter((option) => {
    const tags = (option.optional_value_1 || '').toString().toLowerCase()
    if (type === 'full_ssn') {
      return tags.includes('ssn')
    } else if (type === 'full_bank_account_number') {
      return tags.includes('bank_account_num')
    } else if (type === 'general_document' || type === 'bankruptcy_document' || type === 'loan_agreement') {
      return tags.includes('general_document')
    }
    return false
  })

  const fetchSensitiveData = async (viewReasonId = null) => {
    const thisRequest = ++requestId.current
    const isStale = () => requestId.current !== thisRequest
    try {
      setIsLoading(true)
      let params = {}
      if (loanId) {
        params["loanId"] = loanId
      }

      if (bankAccountId) {
        params["bankAccountId"] = bankAccountId
      }

      if (viewReasonId) {
        params["sensitiveDataViewReasonId"] = viewReasonId
      }

      let url = `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/${type}${documentId ? '/' + documentId : ''}`;
      url += buildQueryString(params)

      requests.setEndpoint(url)
      const response = await requests.get()
      // A close or a newer opening superseded this reveal — never show it.
      if (isStale()) return null

      // If the operation was successful, return the sensitive data
      if (response.status === 200 || response.status === 201) {
        if (type === 'loan_agreement' || type === 'bankruptcy_document' || type === 'general_document') {
          window.open(response.data.data.sensitive_data, "_blank", "noreferrer");
          setIsLoading(false);
           dispatch(setModal({
              is_active: false,
              type: 'sensitive_pii_view_reason_modal'
           }));
          return null;
        } else {
          const sensitiveData = response.data.data.sensitive_data
          setSensitiveData(sensitiveData)
          return sensitiveData
        }
      }
    } catch (error) {
      if (isStale()) return null
      // If an exception occurs, show an error notification
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      setSensitiveData(null)
    } finally {
      if (!isStale()) setIsLoading(false)
    }
  }

  // No auto-fetch for either type - both require form submission

  const resetState = () => {
    requestId.current += 1
    setSensitiveData(null)
    setIsLoading(false)
    reset()
  }

  // This modal stays mounted in the Footer between openings. Clear any revealed value and the
  // reason on close and on every opening (a different customer, type, loan, bank account or
  // document), and bump requestId so a reveal still in flight from the last opening is dropped.
  useEffect(() => {
    resetState()
  }, [isActive, customerId, type, loanId, bankAccountId, documentId])

  if (!isActive) {
    return null
  }

  const onSubmit = async (data) => {
    await fetchSensitiveData(data.sensitive_pii_view_reason_dropdown_option_id?.value)
  }

  return modal.is_active && modal.type === 'sensitive_pii_view_reason_modal' ? (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">
            {(() => {
              let dataType
              if (type === 'full_ssn') {
                dataType = 'Full Social Security Number'
              } else if (type === 'full_bank_account_number') {
                dataType = 'Full Bank Account Number'
              } else {
                dataType = `Document`
              }
              return !customerId ? dataType : `${dataType} for Customer #${customerId}`
            })()}
          </p>
          <button
            className="delete"
            aria-label="close"
            onClick={onClose}
          ></button>
        </header>
        {(() => {
          if (!sensitiveData) {
            let dataTypeLabel
            if (type === 'full_ssn') {
              dataTypeLabel = 'Full SSN'
            } else if (type === 'full_bank_account_number') {
              dataTypeLabel = 'Full Bank Account Number'
            } else {
              dataTypeLabel = `${documentName} Document`
            }
            let requiredMessage
            if (type === 'full_ssn') {
              requiredMessage = 'Please select a reason for viewing the full SSN.'
            } else if (type === 'full_bank_account_number') {
              requiredMessage = 'Please select a reason for viewing the full bank account number.'
            } else {
              requiredMessage = `Please select a reason for viewing the ${documentName} document.`
            }

            return (
              <form onSubmit={handleSubmit(onSubmit)}>
                <section className="modal-card-body">
                  <div className="field">
                    <label className="label has-text-weight-normal" htmlFor="sensitive-pii-view-reason-select">
                      Reason For Viewing {dataTypeLabel} <span className="has-text-danger">*</span>
                    </label>
                    <div className="control">
                      <Controller
                        name="sensitive_pii_view_reason_dropdown_option_id"
                        control={control}
                        rules={{ required: requiredMessage }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            inputId="sensitive-pii-view-reason-select"
                            options={sensitivePiiViewReasonOptions}
                            placeholder="Select..."
                            isSearchable={false}
                            classNames={reactSelectClassNames()}
                            onChange={(val) => {
                              field.onChange(val)
                            }}
                            menuPosition="fixed"
                            styles={{
                              container: (base) => ({ ...base, width: '100%' }),
                              menu: (css) => ({ ...css, width: '100%' }),
                            }}
                          />
                        )}
                      />
                    </div>
                    {errors.sensitive_pii_view_reason_dropdown_option_id && (
                      <p className="help is-danger">
                        {errors.sensitive_pii_view_reason_dropdown_option_id.message}
                      </p>
                    )}
                  </div>
                </section>
                <footer className="modal-card-foot is-justify-content-end py-1">
                  <div className="lox-form__buttons-wrapper has-text-right">
                    <button
                      type="button"
                      className="button is-small has-background-grey has-text-white"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="button is-link is-small"
                      disabled={!isValid || isLoading}
                    >
                      {isLoading ? 'Loading...' : 'Show'}
                    </button>
                  </div>
                </footer>
              </form>
            )
          }
          // Show sensitive data after form submission
          return (
            <>
              <section className="modal-card-body">
                <div className="content">
                  <p className="has-text-centered">{sensitiveData}</p>
                </div>
              </section>
              <footer className="modal-card-foot is-justify-content-end py-1">
                <div className="lox-form__buttons-wrapper has-text-right">
                  <button
                    type="button"
                    className="button is-small has-background-grey has-text-white"
                    onClick={onClose}
                  >
                    Done
                  </button>
                </div>
              </footer>
            </>
          )
        })()}
      </div>
    </div>
  ) : null
}
